/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMemoizedFn } from 'ahooks';
import html2canvas from 'html2canvas';
import Decimal from 'decimal.js';
import { useLocation } from 'react-router';
import { Icon } from '@fuxi/eevee-icon';
import { Button } from 'antd-mobile';
import service from '@/service';
import { MassageType2Text, storagePrefixV2 } from '@/constant';
import planExpiredImg from '@/assets/image/load-error.png';
import logoImg from '@/assets/image/home-logo.png';
import { When } from '@/components';
import { THERAPY_TYPE_2_TEXT } from '../../utils';
import { PHYSICAL_CONDITION_ITEMS } from '../physical-condition';
import { PAIN_AREA_CONFIG } from '../mark-pain-area';
import { MARK_BACK_AREA, MARK_SHOULDER_AREA } from '../pain-condition-level';
import { WEIGHT_LOSS_PROBLEM_2_TEXT } from '../result-plan';
import { POSTURE_ADJUST_ITEM_2_TEXT, POSTURE_ADJUST_ITEM_2_URL } from '../posture-condition';

import cx from './mobile.index.module.scss';

interface Props {}

const THERAPY_TYPE_2_LABEL: Record<AIDiagnose.TherapyType, string> = {
  weightLoss: '您的减肥瘦身诉求',
  bodyAdjust: '您想改善的体态问题',
  painRelief: '您的痛症区域',
  physicalImprove: '您的体质',
};

export const formateFrequency = (frequency: number) => {
  if (!frequency) return 0;

  const calcValue = new Decimal(7).div(+frequency || 0);
  return calcValue?.isInteger() ? calcValue?.toNumber() : calcValue?.toFixed(Math.min(calcValue.decimalPlaces(), 2));
};

const ResultPlan: React.FC<Props> = () => {
  const location = useLocation();
  const [imgUrl, setImgUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [AIDiagnoseReport, setAIDiagnoseReport] = useState<GetAIDiagnoseResultResponse>({} as any);
  const [isPlanExpired, setIsPlanExpired] = useState(false);
  const pageRef = useRef(null);

  // 展示痛症舒缓选中区域
  const currentMarkPainAreaShowName = useMemo(
    () =>
      Array.from(
        new Set(
          AIDiagnoseReport?.painReliefList?.map(item =>
            MARK_SHOULDER_AREA.includes(item.problem)
              ? '肩部'
              : MARK_BACK_AREA.includes(item?.problem)
              ? '背部'
              : '腰部'
          )
        )
      )
        ?.sort((a, b) => ['肩部', '背部', '腰部']?.indexOf(a) - ['肩部', '背部', '腰部']?.indexOf(b))
        ?.join('、') || [],
    [AIDiagnoseReport]
  );

  // 选择的减肥诉求
  const currentWeightLoseRquestion = useMemo(() => {
    let defaultText = Array.from(
      new Set(AIDiagnoseReport?.weightLossList?.map(item => WEIGHT_LOSS_PROBLEM_2_TEXT[item?.problem]?.typeName))
    )?.join('、');

    const isShowExtraText = AIDiagnoseReport?.weightLossList?.some(item => item?.currentValue - item.goalValue >= 60);

    if (isShowExtraText) {
      return (
        <>
          {defaultText}
          <span style={{ color: '#000000D9', fontWeight: 'normal' }}>
            ，目标跨度稍大，需要保持一定的理疗频率加强效果哦～
          </span>
        </>
      );
    }

    return defaultText;
  }, [AIDiagnoseReport]);

  // 建议列表
  const advicesList = useMemo(
    () => [
      {
        title: '拉伸建议',
        text: AIDiagnoseReport?.stretchAdvice,
        images: AIDiagnoseReport?.stretchImages,
      },
      {
        title: '活动建议',
        text: AIDiagnoseReport?.activityAdvice,
        images: AIDiagnoseReport?.activityImages,
      },
      {
        title: '饮食建议',
        text: AIDiagnoseReport?.suggestFoodAdvice,
        images: AIDiagnoseReport?.suggestFoodImages,
      },
      {
        title: '饮食禁忌建议',
        text: AIDiagnoseReport?.forbiddenFoodAdvice,
        images: [],
      },
      {
        title: '睡眠建议',
        text: AIDiagnoseReport?.sleepAdvice,
        images: AIDiagnoseReport?.sleepImages,
      },
      {
        title: '生活习惯建议',
        text: AIDiagnoseReport?.habitAdvice,
        images: AIDiagnoseReport?.habitImages,
      },
      {
        title: '居住环境建议',
        text: AIDiagnoseReport?.livingEnvironmentAdvice,
        images: [],
      },
    ],
    [AIDiagnoseReport]
  );

  const getResult = useMemoizedFn(async (sessionId: string) => {
    try {
      const { data } = await service.AIDiagnose.getAIDiagnoseReport(sessionId);

      setAIDiagnoseReport(data);
      setIsPlanExpired(!data);
    } catch {
      setIsPlanExpired(true);
    } finally {
      setIsLoading(false);
    }
  });

  const planTitleName = useMemo(() => {
    const selectTherapyTypes = AIDiagnoseReport?.types;
    if (!selectTherapyTypes?.length) return '';

    if (selectTherapyTypes?.length > 1) {
      return '身体综合改善计划';
    } else {
      return THERAPY_TYPE_2_TEXT[selectTherapyTypes[0]] + '计划';
    }
  }, [AIDiagnoseReport?.types]);

  const screenshotPage = useCallback(async () => {
    const canvas = await html2canvas(pageRef.current, { useCORS: true });
    // 将画布转换为数据 URL
    const imgData = canvas.toDataURL('image/png', 1.0);
    setImgUrl(imgData);
  }, []);

  useEffect(() => {
    document.title = '网易机器人-AI问询报告';
    return () => {
      document.title = '理疗机器人-店员端';
    };
  }, []);

  useEffect(() => {
    if (location?.search) {
      const session = location?.search.split('=')[1];
      if (session) {
        getResult(session);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return (
    <div className={cx('ResultPlan-conatiner')}>
      {isLoading ? (
        <div className={cx('loading-wrapper')}>
          <div className={cx('animation-wrapper')}></div>
          <div className={cx('loading-text')}>正在加载...</div>
        </div>
      ) : isPlanExpired ? (
        <div className={cx('plan-expired')}>
          <img src={planExpiredImg} alt="" />
          <div>二维码不小心过期了</div>
        </div>
      ) : (
        <>
          <div className={cx('ResultPlan-wrapper')} ref={pageRef}>
            <img className={cx('mirror-header-bg')} src={storagePrefixV2 + '/qweqwasdas.png'} alt="" />
            <div className={cx('mirror-bg')} />
            <div className={cx('ResultPlan')}>
              <div className={cx('bg-wrapper')} />
              <div className={cx('header')}>
                <img className={cx('netease-logo')} src={logoImg} alt="" />
                <span className={cx('user-tips')}>
                  Hi，{AIDiagnoseReport?.customerName}，{AIDiagnoseReport?.storeName}已为您制定
                </span>
                <span className={cx('plan-name')}>{planTitleName}</span>
                <div className={cx('plan-tips')}>
                  <div className={cx('tips-header')}>您的理疗诉求</div>
                  <div className={cx('therapy-list')}>
                    {AIDiagnoseReport?.types
                      // 表单排序
                      ?.sort(
                        (a, b) =>
                          Object.keys(THERAPY_TYPE_2_TEXT)?.indexOf(a) - Object.keys(THERAPY_TYPE_2_TEXT)?.indexOf(b)
                      )
                      ?.map(item => (
                        <div className={cx('therapy-item')} key={item}>
                          {THERAPY_TYPE_2_TEXT[item]}
                        </div>
                      ))}
                  </div>
                </div>
              </div>
              <div className={cx('content')}>
                <div className={cx('select-therapy-list')}>
                  {AIDiagnoseReport?.types // 排序
                    ?.sort(
                      (a, b) =>
                        Object.keys(THERAPY_TYPE_2_TEXT)?.indexOf(a) - Object.keys(THERAPY_TYPE_2_TEXT)?.indexOf(b)
                    )
                    ?.map(item => (
                      <div key={item}>
                        <div className={cx('header-with-border')}>
                          <span className={cx('therapy-detail')}>
                            <div className={cx('inner-box')}>
                              <span className={cx('label')}>{THERAPY_TYPE_2_LABEL[item]}：</span>
                              <span>
                                {item === 'painRelief' ? (
                                  <>{currentMarkPainAreaShowName}</>
                                ) : item === 'bodyAdjust' ? (
                                  <>
                                    {AIDiagnoseReport?.bodyAdjustList
                                      ?.sort(
                                        (a, b) =>
                                          Object.keys(POSTURE_ADJUST_ITEM_2_TEXT)?.indexOf(a?.problem) -
                                          Object.keys(POSTURE_ADJUST_ITEM_2_TEXT)?.indexOf(b?.problem)
                                      )
                                      ?.map(item => POSTURE_ADJUST_ITEM_2_TEXT[item?.problem])
                                      ?.join('、')}
                                  </>
                                ) : item === 'weightLoss' ? (
                                  <>{currentWeightLoseRquestion}</>
                                ) : item === 'physicalImprove' ? (
                                  <>{AIDiagnoseReport?.physiqueName}</>
                                ) : null}
                              </span>
                            </div>
                          </span>
                        </div>
                        <div className={cx('therapy-extra-info', item)}>
                          {item === 'painRelief' ? (
                            <div className={cx('pain-area-wrapper')}>
                              {Object.keys(PAIN_AREA_CONFIG).map(item => (
                                <div
                                  className={cx('marker-item', item)}
                                  key={item}
                                  style={{
                                    backgroundImage: `url(${
                                      PAIN_AREA_CONFIG[item as AIDiagnose.MarkPainArea]?.[
                                        AIDiagnoseReport?.painReliefList?.some(item2 => item2?.problem === item)
                                          ? 'selectUrl'
                                          : 'defaultPlainUrl'
                                      ]
                                    })`,
                                  }}
                                />
                              ))}
                            </div>
                          ) : item === 'bodyAdjust' ? (
                            <div className={cx('body-adjust-wrapper')}>
                              {AIDiagnoseReport?.bodyAdjustList
                                ?.sort(
                                  (a, b) =>
                                    Object.keys(POSTURE_ADJUST_ITEM_2_TEXT)?.indexOf(a?.problem) -
                                    Object.keys(POSTURE_ADJUST_ITEM_2_TEXT)?.indexOf(b?.problem)
                                )
                                ?.map(item => (
                                  <div
                                    className={cx('image-item')}
                                    key={item.problem}
                                    style={{ backgroundImage: `url(${POSTURE_ADJUST_ITEM_2_URL[item?.problem]})` }}
                                  ></div>
                                ))}
                            </div>
                          ) : item === 'weightLoss' ? (
                            <div className={cx('weight-loss-wrapper')}>
                              {AIDiagnoseReport?.weightLossList // 表单排序
                                ?.sort(
                                  (a, b) =>
                                    Object.keys(WEIGHT_LOSS_PROBLEM_2_TEXT)?.indexOf(a?.problem) -
                                    Object.keys(WEIGHT_LOSS_PROBLEM_2_TEXT)?.indexOf(b?.problem)
                                )
                                ?.map(item => (
                                  <div className={cx('weightloss-infos-item')} key={item?.problem}>
                                    <div className={cx('left')}>
                                      <span>当前</span>
                                      <span className={cx('highlight')}>{item.currentValue / 10}</span>
                                      <span>厘米</span>
                                    </div>
                                    <div className={cx('part-type-name')}>
                                      {WEIGHT_LOSS_PROBLEM_2_TEXT[item?.problem]?.dimensionName}
                                    </div>
                                    <div className={cx('right')}>
                                      <span>目标</span>
                                      <span className={cx('highlight')}>{item.goalValue / 10}</span>
                                      <span>厘米</span>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          ) : item === 'physicalImprove' ? (
                            <div className={cx('physical-wrapper')}>
                              <div>{AIDiagnoseReport?.physiqueAnalysis}</div>
                              <div>
                                <span>您希望改善的体质问题是：</span>
                                <span className={cx('highlight')}>
                                  {AIDiagnoseReport?.physicalImproveList
                                    ?.sort(
                                      (a, b) =>
                                        Object.keys(PHYSICAL_CONDITION_ITEMS)?.indexOf(a?.problem) -
                                        Object.keys(PHYSICAL_CONDITION_ITEMS)?.indexOf(b?.problem)
                                    )
                                    ?.map(item => PHYSICAL_CONDITION_ITEMS[item?.problem]?.name)
                                    ?.filter(Boolean)
                                    ?.join('、')}
                                </span>
                              </div>
                              <div className={cx('duty-tips')}>
                                <Icon className={cx('icon')} name="info-circle" />
                                <div>舌诊是中医四诊的部分内容，上述诊断结果仅供参考，不能作为诊断治疗依据</div>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                </div>

                <div className={cx('item-header')}>日常小建议</div>

                {advicesList
                  ?.filter(item => item?.text)
                  ?.map((item, index) => (
                    <div className={cx('advice-wrapper')} key={index}>
                      <div className={cx('header-with-border')}>
                        {item.title}：{item?.text}
                      </div>
                      {!!item?.images?.length && (
                        <div className={cx('food-images')}>
                          {item?.images?.map(item => (
                            <div className={cx('food-image-item')} key={item}>
                              <img src={item} alt="" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                <div className={cx('item-header')}>我的专属方案包</div>

                {!!AIDiagnoseReport?.recommendGoodsList?.length ? (
                  <>
                    <div className={cx('recommend-list')}>
                      {AIDiagnoseReport?.recommendGoodsList?.map(item => (
                        <div className={cx('plan-details-item')} key={item.id}>
                          <div className={cx('plan-detail-left')}>
                            <div
                              className={cx('img-box')}
                              style={{
                                backgroundImage: `url(${item?.wxCoverImage})`,
                              }}
                            >
                              <div className={cx('color-box')}></div>
                              <div className={cx('header-box')}>
                                <div className={cx('header')}>
                                  <div className={cx('name', { 'is-draft': false })}>{item.name}</div>

                                  <div className={cx('header-tag')}>
                                    <div className={cx(item.itemType + '-tags', 'type')}>
                                      {MassageType2Text[item.itemType]}
                                    </div>
                                  </div>
                                </div>

                                {/* 人工套餐无部位区分 */}
                                {false ? (
                                  <div className={cx('only-manual')}>
                                    <Icon name="only-hand" className={cx('icon')} size="12px" />
                                    <span>仅支持人工操作</span>
                                  </div>
                                ) : (
                                  item?.recommendedTime && (
                                    <div className={cx('time')}>{`${item.recommendedTime}分钟`}</div>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                          <div className={cx('plan-detail-right')}>
                            <div className={cx('aim-type')}>
                              <div className={cx('aim-type-label')}>针对</div>
                              <div className={cx('aim-type-value')}>
                                {item?.types
                                  ?.sort(
                                    (a, b) =>
                                      Object.keys(THERAPY_TYPE_2_TEXT)?.indexOf(a) -
                                      Object.keys(THERAPY_TYPE_2_TEXT)?.indexOf(b)
                                  )
                                  ?.map(item => THERAPY_TYPE_2_TEXT[item])
                                  ?.join(' · ')}
                              </div>
                            </div>
                            <When condition={!!item.effectIntro}>
                              <div className={cx('intros')}>{item.effectIntro}</div>
                            </When>
                            {item.itemType === 'Moxibustion' && (
                              <div className={cx('recommand-tip')}>
                                <div className={cx('label')}>艾灸穴位：</div>
                                <div className={cx('value', 'highlight')}>{item.acupoints?.join(' ')}</div>
                              </div>
                            )}
                            <div className={cx('recommend-footer-card')}>
                              <div className={cx('recommend-footer-card-item')}>
                                <div className={cx('label')}>建议理疗疗程：</div>
                                <div className={cx('value')}>
                                  <span className={cx('highlight')}>{item.treatmentCourse}</span>
                                  <span>个疗程</span>
                                  <span className={cx('highlight')}>{item.treatmentCourse * 10}</span>
                                  <span>次以上</span>
                                </div>
                              </div>
                              <div className={cx('recommend-footer-card-item')}>
                                <div className={cx('label')}>建议理疗频率：</div>
                                <div className={cx('value')}>
                                  <span className={cx('highlight')}>1</span>
                                  <span>周</span>
                                  <span className={cx('highlight')}>{formateFrequency(item.frequency)}</span>
                                  <span>次</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className={cx('therapy-tips', 'show-area-2')}>
                      <div>
                        <span>如下情况不建议进行理疗，请及时与理疗师沟通</span>
                      </div>
                      <div className={cx('tips-list-item')}>
                        <div className={cx('circle')} />
                        <span>体内有金属者、佩戴金属饰品、严重骨质疏松患者</span>
                      </div>
                      <div className={cx('tips-list-item')}>
                        <div className={cx('circle')} />
                        <span>局部有皮肤破损、心血管疾病患者</span>
                      </div>
                      <div className={cx('tips-list-item')}>
                        <div className={cx('circle')} />
                        <span>孕妇与生理期、哺乳期女性</span>
                      </div>
                      <div className={cx('tips-list-item')}>
                        <div className={cx('circle')} />
                        <span>空腹、醉酒等状态下不宜使用</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className={cx('empty-plan')}>理疗师将与您亲自沟通理疗方案~</div>
                )}
              </div>
            </div>
          </div>
          <div className={cx('footer')}>
            <Button className={cx('btn')} color="primary" onClick={screenshotPage}>
              一键生成长图
            </Button>
          </div>
          {imgUrl && (
            <div className={cx('save-img-wrapper')}>
              <div className={cx('img-wrapper')}>
                <Icon name="close" className={cx('close-icon')} onClick={() => setImgUrl('')} />
                <img src={imgUrl} alt="" />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ResultPlan;
